import { useTranslate } from "@tolgee/react";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { externalContentRootState } from "../../../pages/externalContent";

interface Props {
  setCurrentView: React.Dispatch<React.SetStateAction<string>>;
  currentView: string;
}

const SuccessView: React.FC<Props> = () => {
  const { t } = useTranslate();

  const config = useSelector(
    (state: externalContentRootState) =>
      state.externalContent.externalContentConfig
  );
  const locationName: string =
    (config as any)?.location?.name || (config as any)?.name || "";
  const locationImage: string =
    (config as any)?.logo_url || (config as any)?.location?.image || "";

  // Mount-time animation flag so the check + content fade in on first render.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const subtitle = locationName
    ? t("externalContent.success.subtitle", { location: locationName })
    : t("externalContent.success.subtitleFallback");

  const steps: { icon: string; key: string }[] = [
    { icon: "fa-paper-plane", key: "externalContent.success.next.review" },
    { icon: "fa-list-check", key: "externalContent.success.next.publish" },
    { icon: "fa-envelope-open", key: "externalContent.success.next.contact" },
  ];

  return (
    <>
      <style>{`
        @keyframes successCheckPop {
          0% { transform: scale(0.3); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes successFadeUp {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .success-check-circle {
          animation: successCheckPop 480ms cubic-bezier(0.22, 1, 0.36, 1);
        }
        .success-fade-up {
          animation: successFadeUp 360ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
      `}</style>
      <div
        className="w-100 d-flex flex-column gap-4 bg-white rounded-4 mx-auto p-4 p-md-5"
        style={{ maxWidth: 720 }}
      >
        {(locationImage || locationName) && (
          <div
            className="d-flex flex-row align-items-center gap-3 pb-3"
            style={{ borderBottom: "1px solid rgba(15, 23, 42, 0.08)" }}
          >
            {locationImage ? (
              <img
                src={locationImage}
                alt={locationName || ""}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  objectFit: "cover",
                  flexShrink: 0,
                  border: "1px solid rgba(15, 23, 42, 0.06)",
                }}
              />
            ) : (
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 8,
                  backgroundColor: "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <i
                  className="fa-duotone fa-location-dot text-primary"
                  style={{ fontSize: "1.2rem" }}
                ></i>
              </div>
            )}
            <div className="d-flex flex-column">
              <span
                className="text-uppercase fw-semibold"
                style={{
                  fontSize: "0.7rem",
                  letterSpacing: "0.06em",
                  color: "#94a3b8",
                  lineHeight: 1,
                }}
              >
                {t("externalContent.submittingTo")}
              </span>
              <span
                className="fw-semibold"
                style={{ fontSize: "1rem", color: "#0f172a", lineHeight: 1.4 }}
              >
                {locationName || t("externalContent.thisLocation")}
              </span>
            </div>
          </div>
        )}

        <div className="d-flex flex-column align-items-center text-center gap-3 py-3 py-md-4">
          <div
            className={mounted ? "success-check-circle" : ""}
            style={{
              width: 96,
              height: 96,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, #d1fadf 0%, #b3f1c8 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 8px 24px rgba(16, 185, 129, 0.18)",
              opacity: mounted ? 1 : 0,
            }}
          >
            <i
              className="fa-solid fa-circle-check"
              style={{ fontSize: "3rem", color: "#10b981" }}
            ></i>
          </div>

          <h1
            className={mounted ? "success-fade-up m-0 fw-bold" : "m-0 fw-bold"}
            style={{
              fontSize: "1.9rem",
              lineHeight: 1.15,
              color: "#0f172a",
              animationDelay: "120ms",
              opacity: mounted ? 1 : 0,
            }}
          >
            {t("externalContent.success.title")}
          </h1>

          <p
            className={
              mounted
                ? "success-fade-up m-0 text-muted"
                : "m-0 text-muted"
            }
            style={{
              fontSize: "1.05rem",
              maxWidth: "44ch",
              animationDelay: "200ms",
              opacity: mounted ? 1 : 0,
            }}
          >
            {subtitle}
          </p>
        </div>

        <div
          className={
            mounted ? "success-fade-up d-flex flex-column gap-3" : "d-flex flex-column gap-3"
          }
          style={{
            animationDelay: "320ms",
            opacity: mounted ? 1 : 0,
          }}
        >
          <span
            className="text-uppercase fw-semibold align-self-center"
            style={{
              fontSize: "0.72rem",
              letterSpacing: "0.06em",
              color: "#64748b",
            }}
          >
            {t("externalContent.success.next.heading")}
          </span>
          <div
            className="d-flex flex-column gap-2 p-3 rounded"
            style={{
              backgroundColor: "#f8fafc",
              border: "1px solid rgba(15, 23, 42, 0.06)",
            }}
          >
            {steps.map((step) => (
              <div
                key={step.key}
                className="d-flex flex-row align-items-center gap-3"
              >
                <div
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "#ffffff",
                    color: "#3699ff",
                    border: "1px solid rgba(54, 153, 255, 0.18)",
                  }}
                >
                  <i className={`fa-duotone ${step.icon}`}></i>
                </div>
                <span style={{ color: "#0f172a", fontSize: "0.95rem" }}>
                  {t(step.key)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div
          className={
            mounted
              ? "success-fade-up d-flex flex-column align-items-center gap-2 mt-2"
              : "d-flex flex-column align-items-center gap-2 mt-2"
          }
          style={{
            animationDelay: "440ms",
            opacity: mounted ? 1 : 0,
          }}
        >
          <Button
            variant="primary"
            size="lg"
            className="w-100"
            onClick={() => {
              // Full reload clears the previous submission's media + text
              // from redux. localStorage survives, so remembered contributors
              // land on the WelcomeBack card and only need one tap to start
              // their next submission.
              window.location.reload();
            }}
          >
            <i className="fa-regular fa-plus me-2"></i>
            {t("externalContent.success.submitAnother")}
          </Button>
          <span className="text-muted small">
            {t("externalContent.success.close")}
          </span>
        </div>
      </div>
    </>
  );
};

export default SuccessView;
